import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { IAgingCommentsDialogUpdate, IUpdateAgingCommentsOnInvoice } from 'src/app/shared/interfaces';

@Component({
  selector: 'app-aging-comments-update',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    ReactiveFormsModule,
  ],
  templateUrl: './aging-comments-update.component.html',
  styleUrl: './aging-comments-update.component.scss',
})
export class AgingCommentsUpdateComponent {
  readonly dialogRef = inject(MatDialogRef<AgingCommentsUpdateComponent>);
  readonly data = inject<IAgingCommentsDialogUpdate>(MAT_DIALOG_DATA);
  comments: string = '';
  invoiceNumber: string = '';

  //Services
  _appGeneralState = inject(AppGeneralStateService);
  _dataService = inject(DataService);
  _snackBar = inject(MatSnackBar);
  _destroyRefService = inject(DestroyRef);
  _messageDialog = inject(MessageDialogService);

  commentsForm = new FormGroup({
    comments: new FormControl(this.data.comments, [Validators.maxLength(2000)]),
  });

  constructor() {
    this.comments = this.data.comments;
    this.invoiceNumber = this.data.invoiceNumber;
  }

  onSubmitCommentsUpdate(): void {
    let invoiceUpdate: IUpdateAgingCommentsOnInvoice = {} as IUpdateAgingCommentsOnInvoice;
    invoiceUpdate.invoiceNumber = this.invoiceNumber;
    invoiceUpdate.comments = this.commentsForm.get('comments').value;
    invoiceUpdate.userName = this._appGeneralState.userName;
    this.updateComments(invoiceUpdate, false); //false = don't clear out values after update
  }

  // This method is called from the html form to clear out any comments and update the db
  onClearExistingComments(): void {
    let invoiceUpdate: IUpdateAgingCommentsOnInvoice = {} as IUpdateAgingCommentsOnInvoice;
    invoiceUpdate.invoiceNumber = this.invoiceNumber;
    invoiceUpdate.comments = '';
    invoiceUpdate.userName = this._appGeneralState.userName;
    this.updateComments(invoiceUpdate, true); // true = clear our values on formcontrol after update
  }

  //Call to db to update comments
  //If clearComments = true and call is successful, then remove the comments in form
  updateComments(commentData: IUpdateAgingCommentsOnInvoice, clearComments: boolean) {
    this._dataService
      .updateInvoiceAgingComments(commentData)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {},
        error: (err: any) => {
          this._messageDialog.snackBarTopCenterTimed(
            'An unexpected error occurred and the comments were not updated.  Please try again',
            5000,
          );
        },
        complete: () => {
          this.comments = commentData.comments;
          // if the clear comments variable is true, then clear out the formControl Comment Value
          if (clearComments) {
            this.commentsForm.patchValue({
              comments: '',
            });
          }
          this._messageDialog.snackBarTopCenterTimed('The comments have been successfully updated', 3000);
        },
      });
  }
}
